/* =============================================================================
 * How this company writes a date
 * -----------------------------------------------------------------------------
 * Every date in the app went through one hard-coded shape — 22 Aug 2026 — which
 * is a fine shape and is not everybody's. An accounts office that has written
 * 22/08/2026 on paper for fifteen years reads a screen full of "22 Aug 2026" as
 * a translation of its own records, and a company used to March 22, 2026 reads
 * it as somebody else's.
 *
 * -----------------------------------------------------------------------------
 * Why these are built by hand rather than left to toLocaleDateString
 * -----------------------------------------------------------------------------
 * A locale is not a format. Asking for 'en-GB' gets whatever that browser's
 * version of ICU decided this year, which is why the same page can print two
 * different dates on two machines in the same office. These produce the same
 * characters everywhere, which is the whole point of choosing one.
 *
 * The one genuinely dangerous pair is DD/MM and MM/DD. 03/04/2026 is two
 * different days and nothing on the page says which, so both are labelled with
 * an unambiguous example rather than a pattern nobody parses in their head.
 * ========================================================================== */

/* The holder rather than i18n.tsx, and with the extension Deno insists on.
   This file is reached from an Edge Function — docStrings imports it, the daily
   attendance email imports docStrings — and importing i18n.tsx here put React at
   the end of that chain, which refused the deploy. */
import { currentLanguage } from './activeLanguage.ts';

export type DateFormatId =
  | 'dd-mmm-yyyy' | 'dd/mm/yyyy' | 'mm/dd/yyyy' | 'yyyy-mm-dd'
  | 'dd-mm-yyyy' | 'd-mmmm-yyyy' | 'mmmm-d-yyyy' | 'ddth-mmmm-yyyy'
  | 'dd-mmm-yy' | 'mmm-dd-yyyy';

interface DateFormat {
  id: DateFormatId;
  /** What it is called in the settings list. */
  label: string;
  /** The same date in this shape, so nobody has to decode a pattern. */
  example: string;
  render: (parts: Parts, names: Names) => string;
}

/* -----------------------------------------------------------------------------
 * The words inside a date
 * -----------------------------------------------------------------------------
 * A format decides the arrangement — day first, month first, slashes, spelled
 * out. The language decides what the month is called inside it. The two are
 * separate: a company that writes "22 Aug 2026" and exports an Arabic payslip
 * wants "22 أغسطس 2026", the same arrangement in its own words, not a different
 * format and not an English month sitting in an Arabic line.
 *
 * Gregorian months in the spelling actually used across the Gulf — يناير rather
 * than the Levantine كانون الثاني. These documents are read in the UAE.
 * -------------------------------------------------------------------------- */
interface Names {
  short: string[];
  long: string[];
  /** 1st, 2nd, 3rd — Arabic has no such suffix, so there it is just the number. */
  ordinal: (day: number) => string;
}

interface Parts {
  year: number;
  month: number;
  day: number;
}

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTHS_SHORT_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

const pad = (n: number) => String(n).padStart(2, '0');

/** 1st, 2nd, 3rd, 4th … and the three in the teens that break the rule. */
function ordinal(day: number): string {
  if (day >= 11 && day <= 13) return `${day}th`;
  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
}

const EN: Names = { short: MONTHS_SHORT, long: MONTHS_LONG, ordinal };

/* The short and long forms are the same word in Arabic — these month names are
   not abbreviated in practice, and inventing an abbreviation nobody writes
   would be worse than repeating the full one. */
const AR: Names = {
  short: MONTHS_SHORT_AR,
  long: MONTHS_SHORT_AR,
  ordinal: (day) => String(day),
};

export const DATE_FORMATS: DateFormat[] = [
  {
    id: 'dd-mmm-yyyy',
    label: 'Day, short month, year',
    example: '22 Aug 2026',
    render: (p, n) => `${pad(p.day)} ${n.short[p.month - 1]} ${p.year}`,
  },
  {
    id: 'dd/mm/yyyy',
    // Named for the ambiguity, not the pattern. Somebody choosing between this
    // and the next one has to be able to tell them apart at a glance.
    label: 'Day first, slashes',
    example: '22/08/2026  (22 August)',
    render: (p) => `${pad(p.day)}/${pad(p.month)}/${p.year}`,
  },
  {
    id: 'mm/dd/yyyy',
    label: 'Month first, slashes',
    example: '08/22/2026  (22 August)',
    render: (p) => `${pad(p.month)}/${pad(p.day)}/${p.year}`,
  },
  {
    id: 'dd-mm-yyyy',
    label: 'Day first, dashes',
    example: '22-08-2026  (22 August)',
    render: (p) => `${pad(p.day)}-${pad(p.month)}-${p.year}`,
  },
  {
    id: 'yyyy-mm-dd',
    label: 'Year first',
    example: '2026-08-22',
    render: (p) => `${p.year}-${pad(p.month)}-${pad(p.day)}`,
  },
  {
    id: 'dd-mmm-yy',
    label: 'Day, short month, short year',
    example: '22 Aug 26',
    render: (p, n) => `${pad(p.day)} ${n.short[p.month - 1]} ${String(p.year).slice(2)}`,
  },
  {
    id: 'mmm-dd-yyyy',
    label: 'Short month, day, year',
    example: 'Aug 22, 2026',
    render: (p, n) => `${n.short[p.month - 1]} ${pad(p.day)}, ${p.year}`,
  },
  {
    id: 'd-mmmm-yyyy',
    label: 'Day, full month, year',
    example: '22 August 2026',
    render: (p, n) => `${p.day} ${n.long[p.month - 1]} ${p.year}`,
  },
  {
    id: 'mmmm-d-yyyy',
    label: 'Full month, day, year',
    example: 'August 22, 2026',
    render: (p, n) => `${n.long[p.month - 1]} ${p.day}, ${p.year}`,
  },
  {
    id: 'ddth-mmmm-yyyy',
    label: 'Ordinal day, full month, year',
    example: '22nd August 2026',
    render: (p, n) => `${n.ordinal(p.day)} ${n.long[p.month - 1]} ${p.year}`,
  },
];

export const DEFAULT_DATE_FORMAT: DateFormatId = 'dd-mmm-yyyy';

/**
 * Narrows a value read from the database.
 *
 * The column is text and this is a fixed list, so a value that is not on the
 * list has to resolve to the default rather than be passed along as though it
 * were valid. Same shape as isLanguage.
 */
export function isDateFormat(value: unknown): value is DateFormatId {
  return DATE_FORMATS.some((format) => format.id === value);
}

const BY_ID = new Map(DATE_FORMATS.map((f) => [f.id, f]));

/* The company's choice, held where every caller can reach it.
   -----------------------------------------------------------------------------
   The same shape setActiveTimeZone already uses in dates.ts, and for the same
   reason: formatDate is called from spreadsheet writers and PDF builders as well
   as components, and threading a format through sixteen files of call sites to
   reach a pure display function would be a lot of plumbing for one setting.

   Set once when the session loads. Anything rendered before that — a login
   screen — uses the default, which is what everything used before this existed. */
let activeFormat: DateFormatId = DEFAULT_DATE_FORMAT;

export function setActiveDateFormat(format: DateFormatId | null | undefined): void {
  activeFormat = format && BY_ID.has(format) ? format : DEFAULT_DATE_FORMAT;
}

export function currentDateFormat(): DateFormatId {
  return activeFormat;
}

/**
 * Splits a stored date without going through Date.
 *
 * `new Date('2026-08-22')` is parsed as midnight UTC and then printed in the
 * viewer's zone, so west of Greenwich it prints the 21st. Every date this system
 * stores is a calendar date — a work date, a joining date, an expiry — and a
 * calendar date has no time and no zone. Reading the string directly is the only
 * way it survives the journey.
 */
function partsOf(date: string): Parts | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date.trim());
  if (!m) return null;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/**
 * A stored date in the company's chosen shape.
 *
 * Anything unparseable comes back as it was rather than as "Invalid Date" — a
 * raw value at least tells somebody what is in the field.
 */
export function formatDateAs(
  date: string | null | undefined,
  format: DateFormatId = activeFormat,
  empty = '—',
  /**
   * Which language the month name is written in.
   *
   * Defaults to the reader's, not to English. It defaulted to English, which
   * meant every screen that simply asked for a date — which is nearly all of
   * them — printed "30 May 2026" on an Arabic page. The shape was right and
   * the words were not, which is the kind of wrong that survives a long time
   * because it looks deliberate.
   *
   * The exports and the PDFs pass this explicitly, because a document's
   * language is the one chosen for that document rather than the one the
   * person happens to be reading the app in.
   *
   * Read rather than passed: this is called from components, from table cells
   * and from plain helpers that are not components at all, and a parameter on
   * each would be a hundred chances to forget one — the same reasoning as
   * formatMoney.
   */
  lang: 'en' | 'ar' = currentLanguage() === 'ar' ? 'ar' : 'en',
): string {
  if (!date) return empty;

  const parts = partsOf(date);
  if (!parts) return date;

  return (BY_ID.get(format) ?? BY_ID.get(DEFAULT_DATE_FORMAT)!)
    .render(parts, lang === 'ar' ? AR : EN);
}

/** The example beside a format in the settings list. */
export function exampleOf(format: DateFormatId): string {
  return BY_ID.get(format)?.example ?? '';
}

/** The same date in the same shape, written in Arabic. */
export function exampleInArabic(format: DateFormatId): string {
  return formatDateAs('2026-08-22', format, '', 'ar');
}

/** A month's short name, 1 to 12, in either language — the same words the dates use. */
export function monthName(month: number, lang: 'en' | 'ar'): string {
  return (lang === 'ar' ? AR : EN).short[month - 1] ?? '';
}
